import React, { useEffect } from 'react';
import { useUploadPhotoFromUrlMutation, useCreateMediaPhotoMutation } from '@graphql/generated';
import { useFilestack } from '@shared/utils/filestack';
import { useState } from 'react';
import { useToast } from '@withjoy/joykit';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { addError } from '@shared/utils/logger';

export function useImageFilePicker({ eventId, initialImageUrl }: { eventId: string; initialImageUrl?: string }) {
  const [updatedImage, setUpdatedImage] = useState<string | undefined>(initialImageUrl);
  const [imageName, setImageName] = useState<string>('');

  // Sync state when initialImageUrl changes (e.g., when fundData is loaded or user selects different fund)
  useEffect(() => {
    setUpdatedImage(initialImageUrl);
    // Reset imageName when switching to a new initial image (not a user-uploaded one)
    setImageName('');
  }, [initialImageUrl]);
  const { toast } = useToast();
  const [uploadPhotoFromUrl, { loading: uploadImageFromUrlLoading }] = useUploadPhotoFromUrlMutation();
  const [createMediaPhoto] = useCreateMediaPhotoMutation();
  const { t, tq } = useTranslation('imageFilePicker');
  const toastPhotoUploadFailed = t('toast', 'toastPhotoUploadFailed');
  const clearImage = () => {
    setUpdatedImage(undefined);
    setImageName('');
  };

  const uploadImage = async (): Promise<{ id: string; url: string } | undefined> => {
    try {
      if (!updatedImage || !imageName) return;

      const { data } = await uploadPhotoFromUrl({
        variables: {
          payload: {
            assetId: imageName,
            containerId: eventId,
            url: updatedImage,
          },
        },
      });
      if (data?.uploadPhotoFromUrl?.id && data?.uploadPhotoFromUrl?.url) {
        const photoId = data.uploadPhotoFromUrl.id;

        try {
          await createMediaPhoto({
            variables: {
              payload: {
                eventId,
                photoId,
              },
            },
            refetchQueries: ['GetEventMedia'],
            awaitRefetchQueries: true,
          });
        } catch (mediaPhotoError) {
          addError((mediaPhotoError as Error).message, {
            eventId,
            context: 'useImageFilePicker.createMediaPhoto',
            photoId,
          });
          toast(tq`Image uploaded but could not be added to gallery. The image is still usable.`, {
            icon: <WarningTriangle />,
          });
        }

        return data.uploadPhotoFromUrl;
      } else {
        throw new Error(toastPhotoUploadFailed());
      }
    } catch (e) {
      addError((e as Error).message, { eventId, context: 'usePhotoFilePicker', url: updatedImage, assetId: imageName });
      toast(`${(e as Error).message}`, { icon: <WarningTriangle /> });
      return;
    }
  };

  const { open: openFilepicker, getPhotoPreviewUrl } = useFilestack({
    containerId: eventId,
    onUploadDone: async res => {
      const { filename, url } = res.filesUploaded[0];
      const image = await getPhotoPreviewUrl({
        url,
      });
      setUpdatedImage(image);
      setImageName(filename);
    },
    maxFiles: 1,
    accept: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  });

  /**
   * Set the image URL programmatically (e.g., from URL metadata unfurl).
   * This sets the preview image without uploading - actual upload happens on form submit via uploadImage().
   * @param imageUrl - The URL of the image to display
   * @param filename - Optional filename. If not provided, extracts from URL or generates a default.
   */
  const setImageFromUrl = async (imageUrl: string, filename?: string) => {
    const derivedFilename = filename ?? (await extractFilenameFromUrl(imageUrl));
    setUpdatedImage(imageUrl);
    setImageName(derivedFilename);
  };

  return {
    openFilepicker,
    clearImage,
    updatedImage,
    uploadImage,
    uploadImageFromUrlLoading,
    imageName,
    // Setters for programmatic image setting
    setUpdatedImage,
    setImageName,
    setImageFromUrl,
  };
}

const MIME_TO_EXTENSION_MAP: Readonly<Record<string, string>> = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
  'image/svg+xml': 'svg',
  'image/bmp': 'bmp',
  'image/avif': 'avif',
  'image/tiff': 'tiff',
};

/**
 * Maps a MIME content type to a file extension.
 * @param contentType - The MIME type (e.g., 'image/png')
 * @returns The corresponding file extension (e.g., 'png')
 */
function mimeTypeToExtension(contentType: string): string {
  // Extract base MIME type (strip charset, etc.)
  const baseMime = (contentType || '').split(';')[0].trim().toLowerCase();
  return MIME_TO_EXTENSION_MAP[baseMime] ?? 'jpg';
}

/**
 * Fetches the Content-Type header from a URL via HEAD request.
 * @param url - The URL to fetch headers from
 * @returns The extension derived from Content-Type, or undefined if fetch fails
 */
async function fetchImageExtension(url: string): Promise<string | undefined> {
  try {
    const response = await fetch(url, { method: 'HEAD' });
    const contentType = response.headers.get('Content-Type');
    if (contentType?.startsWith('image/')) {
      return mimeTypeToExtension(contentType);
    }
  } catch {
    // Network error or CORS - fall through to undefined
  }
  return undefined;
}

/**
 * Extracts a filename from a URL, or generates a default if extraction fails.
 * Uses HEAD request to determine actual image type when URL path has no extension.
 * @param url - The URL to extract the filename from
 * @returns The extracted filename or a generated default
 */
async function extractFilenameFromUrl(url: string): Promise<string> {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    const segments = pathname.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1];

    if (lastSegment && /\.(jpg|jpeg|png|gif|webp|svg|bmp|avif|tiff)$/i.test(lastSegment)) {
      return lastSegment;
    }

    // No extension in URL path - fetch Content-Type to determine actual format
    const extension = (await fetchImageExtension(url)) ?? 'jpg';
    const hostname = urlObj.hostname.replace(/\./g, '-');
    return `${hostname}-${Date.now()}.${extension}`;
  } catch {
    return `unfurled-image-${Date.now()}.jpg`;
  }
}
